import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { CommandBlock } from "@/components/CommandBlock";
import { SetupStep } from "@/components/SetupStep";
import { useConfirm } from "@/components/ConfirmDialog";
import { Toast, useToast } from "@/components/Toast";
import { SettingsSection, SettingsSkeleton } from "./SettingsSection";
import { useApi } from "@/lib/api";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";
import { isConfigured, isEnabled } from "@/lib/enums";
import { GoogleDirectorySteps } from "./GoogleDirectorySteps";
import { providerLabels, supportsScim, type ScimStatus } from "./shared";

export function ScimSettings({
  ssoEnabled,
  idpProviderHint,
}: {
  ssoEnabled: boolean;
  idpProviderHint: string | null;
}) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { toast, showToast } = useToast();
  const { orgId } = useParams();
  const [scimStatus, setScimStatus] = useState<ScimStatus | null>(null);
  const [scimError, setScimError] = useState<string | null>(null);
  const [scimSubmitting, setScimSubmitting] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { copiedField, copyToClipboard } = useCopyToClipboard();
  const usesScim = supportsScim(idpProviderHint);
  const label = usesScim ? "SCIM" : "Directory sync";

  const fetchScimStatus = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    return api.get<ScimStatus>(`/organizations/${orgId}/scim`);
  }, [accessToken, api, orgId]);

  const applyScimStatus = useCallback((data: ScimStatus | null) => {
    if (data) {
      setScimStatus(data);
      setScimError(null);
    } else {
      setScimError("Couldn't load SCIM status.");
    }
  }, []);

  useEffect(() => {
    if (!ssoEnabled) {
      return;
    }

    let cancelled = false;

    void fetchScimStatus().then((data) => {
      if (!cancelled) {
        applyScimStatus(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [ssoEnabled, fetchScimStatus, applyScimStatus]);

  async function handleScimCreate() {
    if (!accessToken || !orgId) {
      return;
    }

    setScimSubmitting(true);
    setScimError(null);

    try {
      const body = await api.send<{ scimSecret: string }>(
        `/organizations/${orgId}/scim`,
        "POST",
        undefined,
        "Couldn't configure SCIM.",
      );

      setRevealedSecret(body.scimSecret);
      applyScimStatus(await fetchScimStatus());
      showToast("SCIM credentials generated.", "success");
    } catch (err) {
      setScimError(
        err instanceof Error ? err.message : "Couldn't configure SCIM.",
      );
    } finally {
      setScimSubmitting(false);
    }
  }

  async function handleScimRotate() {
    if (!accessToken || !orgId) {
      return;
    }

    const confirmed = await confirm(
      "Rotating the SCIM token will prevent your SCIM integration from working until you update the token in your identity provider.",
      { title: "Rotate secret", confirmLabel: "Rotate" },
    );

    if (!confirmed) {
      return;
    }

    setScimSubmitting(true);
    setScimError(null);

    try {
      const body = await api.send<{ scimSecret: string }>(
        `/organizations/${orgId}/scim/rotate`,
        "PATCH",
        undefined,
        "Couldn't rotate the SCIM secret.",
      );

      setRevealedSecret(body.scimSecret);
      applyScimStatus(await fetchScimStatus());
      showToast("SCIM secret rotated.", "success");
    } catch (err) {
      setScimError(
        err instanceof Error ? err.message : "Couldn't rotate the SCIM secret.",
      );
    } finally {
      setScimSubmitting(false);
    }
  }

  async function toggleScimEnabled() {
    if (!accessToken || !orgId || !scimStatus) {
      return;
    }

    setScimSubmitting(true);
    setScimError(null);

    try {
      await api.send(
        `/organizations/${orgId}/scim/${isEnabled(scimStatus.status) ? "disable" : "enable"}`,
        "PATCH",
        undefined,
        "Couldn't update SCIM status.",
      );

      applyScimStatus(await fetchScimStatus());
      showToast(
        isEnabled(scimStatus.status)
          ? `${label} disabled.`
          : `${label} enabled.`,
        "success",
      );
    } catch (err) {
      setScimError(
        err instanceof Error ? err.message : "Couldn't update SCIM status.",
      );
    } finally {
      setScimSubmitting(false);
    }
  }

  async function handleScimDelete() {
    if (!accessToken || !orgId) {
      return;
    }

    const confirmed = await confirm(
      "Are you sure you want to delete this SCIM connection?",
      {
        title: "Delete connection",
        confirmLabel: "Delete",
      },
    );

    if (!confirmed) {
      return;
    }

    setScimSubmitting(true);
    setScimError(null);

    try {
      await api.send(
        `/organizations/${orgId}/scim`,
        "DELETE",
        undefined,
        "Couldn't delete SCIM connection.",
      );

      applyScimStatus(await fetchScimStatus());
      showToast("SCIM connection deleted.", "success");
    } catch (err) {
      setScimError(
        err instanceof Error ? err.message : "Couldn't delete SCIM connection.",
      );
    } finally {
      setScimSubmitting(false);
    }
  }

  return (
    <SettingsSection
      className="mt-8"
      title={usesScim ? "Directory sync (SCIM)" : "Directory sync"}
      description="Provision and deprovision users automatically to keep access in sync with your identity provider."
    >
      {scimError && (
        <p className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {scimError}
        </p>
      )}

      {!scimStatus && ssoEnabled ? (
        <SettingsSkeleton steps={2} />
      ) : (
        <>
          <div className="rounded-lg bg-surface p-4">
            <ol className="list-none">
              <SetupStep
                label="1"
                state={ssoEnabled ? "done" : "current"}
                title="Enable SSO"
              >
                <p className="mt-0.5 text-sm text-foreground/70">
                  {ssoEnabled
                    ? "SSO is enabled for this organization."
                    : "SSO must be enabled before you can configure SCIM."}
                </p>
              </SetupStep>

              {usesScim ? (
                <>
                  <SetupStep
                    label="2"
                    state={
                      !ssoEnabled
                        ? "todo"
                        : isConfigured(scimStatus?.status ?? 0)
                          ? "done"
                          : "current"
                    }
                    title="Generate SCIM credentials"
                  >
                    {!ssoEnabled ? (
                      <p className="mt-0.5 text-sm text-foreground/70">
                        Use these credentials to configure SCIM in your identity
                        provider.
                      </p>
                    ) : !isConfigured(scimStatus?.status ?? 0) ? (
                      <div className="mt-2">
                        <Button
                          variant="primary"
                          onClick={handleScimCreate}
                          disabled={scimSubmitting}
                        >
                          {scimSubmitting
                            ? "Generating..."
                            : "Generate credentials"}
                        </Button>
                      </div>
                    ) : (
                      <p className="mt-0.5 text-sm text-foreground/70">
                        {scimStatus?.idpProviderHint
                          ? (providerLabels[scimStatus.idpProviderHint] ??
                            scimStatus.idpProviderHint)
                          : "Connection created"}
                      </p>
                    )}
                  </SetupStep>

                  <SetupStep
                    label="3"
                    state={
                      isEnabled(scimStatus?.status ?? 0)
                        ? "done"
                        : isConfigured(scimStatus?.status ?? 0)
                          ? "current"
                          : "todo"
                    }
                    title="Configure your identity provider and enable SCIM"
                    last
                  >
                    {!isConfigured(scimStatus?.status ?? 0) ||
                    !scimStatus?.scimEndpoint ? (
                      <p className="mt-0.5 text-sm text-foreground/70">
                        Once enabled, Tendra will keep users in sync with your
                        identity provider.
                      </p>
                    ) : (
                      <>
                        <p className="mt-0.5 mb-3 text-sm text-foreground/70">
                          Paste these values into your identity provider's
                          provisioning settings to start syncing.
                        </p>
                        <div className="mb-2">
                          <label className="mb-1 block text-xs text-foreground">
                            SCIM URL
                          </label>
                          <CommandBlock
                            command={scimStatus.scimEndpoint}
                            field="scimUrl"
                            wrap
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-foreground">
                            SCIM token
                          </label>
                          <div className="flex items-center gap-2">
                            {revealedSecret ? (
                              <CommandBlock
                                className="min-w-0 flex-1"
                                command={revealedSecret}
                                field="scimSecret"
                                wrap
                                copiedField={copiedField}
                                onCopy={copyToClipboard}
                              />
                            ) : (
                              <code className="flex-1 truncate rounded-md border border-muted bg-surface py-3 pl-3 text-sm text-foreground">
                                ················
                              </code>
                            )}
                            <Button
                              variant="secondary"
                              onClick={handleScimRotate}
                              disabled={scimSubmitting}
                            >
                              Rotate secret
                            </Button>
                          </div>
                          {revealedSecret && (
                            <p className="mt-1 text-xs text-foreground/70">
                              Copy this token now and store it securely because
                              you won't be able to view it again.
                            </p>
                          )}
                        </div>
                        <div className="mt-4 flex flex-wrap gap-3 border-t border-muted pt-4">
                          <Button
                            variant="secondary"
                            onClick={toggleScimEnabled}
                            disabled={scimSubmitting}
                          >
                            {isEnabled(scimStatus?.status ?? 0)
                              ? "Disable SCIM"
                              : "Enable SCIM"}
                          </Button>
                        </div>
                      </>
                    )}
                  </SetupStep>
                </>
              ) : (
                <GoogleDirectorySteps
                  orgId={orgId!}
                  ssoEnabled={ssoEnabled}
                  scimStatus={scimStatus}
                  submitting={scimSubmitting}
                  onToggleEnabled={() => void toggleScimEnabled()}
                  onChanged={async () =>
                    applyScimStatus(await fetchScimStatus())
                  }
                  onError={setScimError}
                  onSuccess={(message) => showToast(message, "success")}
                />
              )}
            </ol>
          </div>

          {isConfigured(scimStatus?.status ?? 0) && (
            <div className="mt-4 flex items-center justify-between gap-6 rounded-lg border border-danger/30 p-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Delete this connection
                </h3>
                <p className="mt-0.5 max-w-md text-xs text-foreground/70">
                  Deleting this connection will remove the identity provider.
                  Members will no longer be provisioned or deprovisioned
                  automatically.
                </p>
              </div>
              <Button
                variant="dangerOutline"
                onClick={handleScimDelete}
                disabled={scimSubmitting}
                className="shrink-0"
              >
                Delete connection
              </Button>
            </div>
          )}
        </>
      )}
      {confirmDialog}
      <Toast toast={toast} />
    </SettingsSection>
  );
}
